import { IS_KEYCLOAK_MODE } from '@/features/auth/auth.config';
import useAuthStore from '@/app/store/auth.store';
import { useCurrentUser } from '@damar/admin-hooks';
import type { CurrentUserResponse } from '@damar/admin-hooks';

export const useAuth = () => {
  const authStore = useAuthStore();
  const { data: adminUser, isLoading: adminLoading } = useCurrentUser();
  const currentUser = adminUser as unknown as CurrentUserResponse | undefined;

  if (IS_KEYCLOAK_MODE) {
    const roles = authStore.user?.realm_roles ?? [];
    const isAdminRole = roles.includes('admin') || roles.includes('hr_admin');

    // If user has admin/hr_admin role, grant all permissions (backend BYPASS_SECURITY does the same)
    const keycloakPermissions = isAdminRole && currentUser?.permissions?.actions
      ? { actions: currentUser.permissions.actions.map((a) => ({ ...a, hasAccess: true })) }
      : currentUser?.permissions ?? null;

    const keycloakAdminContext = isAdminRole
      ? { isAdmin: true, canManageUsers: true, canManageTenants: true, canManagePermissions: true, canSyncKeycloak: true }
      : currentUser?.adminContext ?? null;

    return {
      user: authStore.user ? {
        id: authStore.user.sub,
        username: authStore.user.preferred_username,
        email: authStore.user.email,
        firstName: authStore.user.given_name,
        lastName: authStore.user.family_name,
        status: 'ACTIVE',
        tenantId: authStore.user.tenant_id,
      } : null,
      roles,
      isAdminRole,
      permissions: keycloakPermissions,
      adminContext: keycloakAdminContext,
      isAuthenticated: authStore.isAuthenticated,
      isLoading: authStore.isLoading,
      error: authStore.error,
      logout: authStore.logout,
    };
  }

  // Dev mode — always grant full admin access (backend has no real auth)
  const devAdminContext = {
    isAdmin: true,
    canManageUsers: true,
    canManageTenants: true,
    canManagePermissions: true,
    canSyncKeycloak: true,
  };

  return {
    user: currentUser?.userInfo ?? { id: 'dev', username: 'dev', email: 'dev@goflux.com', firstName: 'Dev', lastName: 'User', status: 'ACTIVE', tenantId: 'assi' },
    roles: [] as string[],
    isAdminRole: true,
    permissions: currentUser?.permissions ?? null,
    adminContext: devAdminContext,
    isAuthenticated: true,
    isLoading: adminLoading,
    error: null,
    logout: () => {},
  };
};

export const useCurrentUserInfo = () => {
  const { user } = useAuth();
  return user;
};

export default useAuth;
